import './documentAssistantComponent.scss';
import React, {useEffect, useState} from 'react';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import {useTranslation} from 'react-i18next';
import {getCurrentLanguage} from '../../i18n-service';
import {
    getHibuAssistantRequestWithSearchRequest,
    getLastConversationId,
    uuidv4,
} from '../common/utils';
import {ChatWindow} from '../common/chatWindow';
import {useAiChatStream} from '../common/useAiChatStream';
import {ConditionalRender} from '../../common/conditional-render/ConditionalRender';
import {getEmptySearchRequest} from '../../search/search.utils';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {AiAssistantConversationContext, ChatType, DEFAULT_ASSISTANT_CONVERSATION_CONTEXT, Role,} from '../common/types';
import {useStateConversation, useStateConversationsOfChatType,} from '../duck/assistant-hooks';
import {
    doAddMessageRequest,
    doCreateConversationRequest,
    doDeleteConversationRequest,
    doLoadConversationsRequest,
    doUpdateConversationContextRequest,
} from '../duck/assistant-actions';
import {useStateContainerName} from '../../settings/duck/settings-hooks';
import {MessageBox} from '../../message-box/message-box';
import {showError} from '../../message-box/message-box-actions';
import {ChatInput} from '../common/chatInput';

export const DocumentAssistantComponent = React.memo(() => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const currentLanguage = getCurrentLanguage();

    const containerName = useStateContainerName();
    const emptySearchRequest = getEmptySearchRequest(containerName);

    const conversationsOfChatType = useStateConversationsOfChatType(ChatType.MULTI);
    const [currentConversationId, setCurrentConversationId] = useState<string>(() => {
        return getLastConversationId(conversationsOfChatType, ChatType.MULTI);
    });
    const conversation = useStateConversation(currentConversationId);
    const conversationContext: AiAssistantConversationContext =
        conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;
    const isLoadingConversations = useSelector((state: DefaultRootState) => state.assistant.isLoadingConversations);

    const stream = useAiChatStream();

    useEffect(() => {
        dispatch(doLoadConversationsRequest(ChatType.MULTI));
    }, [dispatch]);

    useEffect(() => {
        if (!conversation && !isLoadingConversations) {
            dispatch(
                doCreateConversationRequest(
                    ChatType.MULTI,
                    currentConversationId,
                    t('assistant.welcome'),
                    Role.ASSISTANT,
                    emptySearchRequest,
                ),
            );
        }
    }, [emptySearchRequest, conversation, currentConversationId, dispatch, t, isLoadingConversations]);

    const fetchResponseAndUpdateConversation = async (
        userMessage: string,
        contextOverride: AiAssistantConversationContext = conversationContext,
    ) => {
        if (!userMessage?.trim() || !conversation) {
            return;
        }

        dispatch(doUpdateConversationContextRequest(currentConversationId, contextOverride));
        dispatch(doAddMessageRequest(currentConversationId, {
            text: userMessage,
            role: Role.USER,
            metadata: {context: contextOverride},
        }));

        const request = getHibuAssistantRequestWithSearchRequest(
            emptySearchRequest,
            currentLanguage,
            userMessage,
            conversation,
            contextOverride,
        );

        return stream.generate(request, {
            onComplete: message => dispatch(doAddMessageRequest(currentConversationId, message, emptySearchRequest)),
            onError: () => dispatch(showError(ChatType.MULTI, 'assistant.error.responseError')),
        });
    };

    const handleNewConversation = () => {
        const newId = uuidv4();
        dispatch(
            doCreateConversationRequest(ChatType.MULTI, newId, t('assistant.welcome'), Role.ASSISTANT, emptySearchRequest),
        );
    };

    const handleDelete = (id: string) => {
        if (id === currentConversationId) {
            stream.cancel();
        }
        dispatch(doDeleteConversationRequest(id));
    };

    if (!conversationsOfChatType) {
        return null;
    }

    return (
        <div id="hibu-assistant-content">
            <MessageBox id={ChatType.MULTI}/>
            <div className="hibu-assistant">
                <div className="conversation-history">
                    <div className={'conversation-wrapper-create'} key={-1} onClick={() => handleNewConversation()}>
                        <div className={'icon-wrapper'}>
                            <SvgIcon icon={Icons.ADD} id="icon-settings" className={['add', 'icon'].join(' ')}/>
                        </div>
                        <div className={'text-wrapper'}>{t('assistant.new-chat')}</div>
                    </div>
                    {conversationsOfChatType.map((conversation) => (
                        <ConditionalRender
                            shouldRender={!!conversation.conversationMessages}
                            children={
                                <div
                                    className={[
                                        'conversation-wrapper',
                                        conversation.id === currentConversationId ? 'active' : '',
                                    ].join(' ')}
                                    key={conversation.id}
                                >
                                    <div
                                        className={'text-wrapper'}
                                        onClick={() => setCurrentConversationId(conversation.id)}
                                    >
                                        {conversation.conversationMessages.find((m) => m.role === 'user')?.text ||
                                            t('assistant.new-chat-title')}
                                    </div>
                                    <div className={'icon-wrapper'} onClick={() => handleDelete(conversation.id)}>
                                        <SvgIcon
                                            icon={Icons.DELETE}
                                            id="icon-settings"
                                            className={['delete', 'icon'].join(' ')}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    ))}
                </div>
                <div className="wrapper">
                    <ChatWindow
                        ref={stream.chatWindowRef}
                        streamedAnswer={stream.text}
                        conversation={conversation}
                        isGeneratingAnswer={stream.isGeneratingAnswer}
                    />
                    <div className="chat-sugestions"></div>
                    <ChatInput
                        conversationId={currentConversationId}
                        welcomeMessage={t('assistant.input-placeholder')}
                        getAiResponse={fetchResponseAndUpdateConversation}
                        handleResetConversation={conversation ? () => handleDelete(conversation.id) : () => null}
                    />
                </div>
            </div>
        </div>
    );
});
